import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { secureLog } from "@/utils/securityUtils";

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  
  const { data: isAdmin, isLoading: rolesLoading } = useQuery({
    queryKey: ['is-admin', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('is_admin_simple');
      
      if (error) throw error;
      return data as boolean;
    },
    enabled: !!user?.id
  });

  if (authLoading || rolesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    secureLog.warn('Unauthorized admin access attempt, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    secureLog.warn('Non-admin user attempted to access admin area', user.id);
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;